/**
 * ImageBlock — full-width image with configurable container width.
 * Perfect for large diagrams, plans/pricing charts, product screenshots.
 *
 * Container sizes:
 *   sm     → max-w-2xl  (~672px)   — narrow, blog-post inline width
 *   md     → max-w-4xl  (~896px)
 *   lg     → max-w-6xl  (~1152px)  — matches the Integrations hub graphic
 *   xl     → max-w-7xl  (~1280px)
 *   full   → no max     (edge-to-edge)
 *
 * top_spacing controls how tightly the block hugs the previous section
 * (useful when the block sits immediately below a hero):
 *   normal  — regular top padding (~32px)
 *   tight   — very little top padding (~8px)
 *   attach  — negative top margin eats the previous section's bottom padding
 */
const SIZES = {
  sm:   "max-w-2xl",
  md:   "max-w-4xl",
  lg:   "max-w-6xl",
  xl:   "max-w-7xl",
  full: "max-w-none",
};

const TOP_SPACING = {
  normal: "pt-4 lg:pt-8",
  tight:  "pt-1 lg:pt-2",
  attach: "-mt-4 lg:-mt-12 pt-0",
};

export default function ImageBlock({ data }) {
  const {
    image,
    alt = "",
    caption = "",
    container_size = "lg",
    rounded = "xl",
    shadow = true,
    background = "white",
    href = "",
    top_spacing = "normal",
    bottom_spacing = "normal",
  } = data || {};

  if (!image) return null;

  const container = SIZES[container_size] || SIZES.lg;
  const roundedCls = rounded === "none" ? "" : `rounded-${rounded}`;
  const shadowCls = shadow ? "shadow-[0_20px_40px_-20px_rgba(15,23,42,0.15)]" : "";
  const bgCls = background === "slate" ? "bg-slate-50" : "bg-white";
  const topCls = TOP_SPACING[top_spacing] || TOP_SPACING.normal;
  const bottomCls =
    bottom_spacing === "tight" ? "pb-4 lg:pb-6" :
    bottom_spacing === "none"  ? "pb-0" :
    "pb-12 lg:pb-16";

  const imgTag = (
    <img
      src={image}
      alt={alt}
      className={`w-full h-auto ${roundedCls} ${shadowCls} block`}
      loading="lazy"
    />
  );

  return (
    <section className={`${topCls} ${bottomCls} ${bgCls} relative`}>
      <div className={`${container} mx-auto px-4 sm:px-6 lg:px-8`}>
        {href ? (
          <a href={href} className="block group">
            {imgTag}
          </a>
        ) : (
          imgTag
        )}
        {caption && (
          <p className="mt-4 text-center text-sm text-slate-500 italic">
            {caption}
          </p>
        )}
      </div>
    </section>
  );
}
